import { inRange } from 'lodash';
import type React from 'react';
import { useCallback, useEffect } from 'react';

import useLayoutEffect from '@swf/core/hooks/useIsomorphicLayoutEffect';

enum Direction {
  LEFT,
  RIGHT,
  BOTH,
  NONE
}

const paddedRange = (n: number, r: number) => inRange(n, r + 1, r - 1);

const fadeDirection = (scrollRef: React.RefObject<HTMLElement>): Direction => {
  const { current } = scrollRef;

  if (current) {
    const { clientWidth, scrollLeft, scrollWidth } = current;
    const scrollMax = scrollWidth - clientWidth;

    if (paddedRange(scrollWidth, clientWidth)) {
      return Direction.NONE;
    }

    if (scrollLeft === 0) {
      return Direction.RIGHT;
    }

    if (scrollLeft < scrollMax - 1) {
      return Direction.BOTH;
    }

    if (paddedRange(scrollLeft, scrollMax)) {
      return Direction.LEFT;
    }
  }

  return Direction.NONE;
};

const applyFadesTo = (targetRef: React.RefObject<HTMLElement>, direction: Direction): void => {
  const { current } = targetRef;

  if (!current) {
    return;
  }

  switch (direction) {
    case Direction.LEFT:
      current.setAttribute('data-fade-direction', 'left');
      break;
    case Direction.RIGHT:
      current.setAttribute('data-fade-direction', 'right');
      break;
    case Direction.BOTH:
      current.setAttribute('data-fade-direction', 'both');
      break;
    default:
      current.removeAttribute('data-fade-direction');
  }
};

const useElementEdgeFade = (
  scrollRef: React.RefObject<HTMLElement>,
  targetElementRef: React.RefObject<HTMLElement>
): void => {
  const handleFade = useCallback(() => {
    const direction = fadeDirection(scrollRef);

    applyFadesTo(targetElementRef, direction);
  }, [scrollRef, targetElementRef]);

  useLayoutEffect(() => {
    if (scrollRef.current) {
      const { clientWidth, scrollWidth } = scrollRef.current;

      // Allow a pixel either side in case of a rounding error.
      if (!paddedRange(scrollWidth, clientWidth)) {
        handleFade();
      }
    }
  }, [handleFade, scrollRef, targetElementRef]);

  useEffect(() => {
    const { current } = scrollRef;
    if (current) {
      current.addEventListener('scroll', handleFade);
      window.addEventListener('resize', handleFade);
    }

    return () => {
      if (current) {
        current.removeEventListener('scroll', handleFade);
        window.removeEventListener('resize', handleFade);
      }
    };
  }, [handleFade, scrollRef, targetElementRef]);
};

export default useElementEdgeFade;
